import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { TownHall } from '../../../../../../../_base-shared/models/Entity/TownHall';
import { TownHallService } from '../../town-hall.service';

@Component({
  selector:    'app-town-hall-list',
  templateUrl: './town-hall-list.component.html',
  styles:      [
    `
        mat-table {
            display: table;
            width: 100%;
        }

        mat-table > tr > td {
            padding-top: 10px;
        }
    `,
  ],
})
export class TownHallListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort, {static: true}) sort: MatSort;
  public isLoading                               = 0;
  public townHalls: MatTableDataSource<TownHall> = new MatTableDataSource<TownHall>([]);
  public displayedColumns: Array<string>;
  public defaultPaginatorConfig: { pageIndex: number, pageSize: number, length: number };
  public paginatorConfig: { pageIndex: number, pageSize: number, length: number };
  public defaultSort: { direction: 'asc' | 'desc', active: 'created_at' };
  public totalResults: number;
  public totalPages: number;
  public pageSelected: boolean;
  // Filters
  public searchFilter: UntypedFormControl               = new UntypedFormControl(null);

  private requestFilters: any;
  private subscriptions: Array<Subscription> = [];

  constructor(private translate: TranslateService,
              private toastr: ToastrService,
              private townHallService: TownHallService) {
  }

  ngOnInit(): void {
    this.displayedColumns = [
      'id',
      'name',
      'email',
      'phone',
      'address_county',
      'address_region',
      'address_city',
      'address_postcode',
      'address_postcode',
      'address_street_address',
      'actions',
    ];

    this.defaultPaginatorConfig = {pageIndex: 0, pageSize: 20, length: 1};
    this.defaultSort            = {direction: 'desc', active: 'created_at'};
    this.paginatorConfig        = this.defaultPaginatorConfig;
    this.requestFilters         = {select_all: 0};
    this.fetchTownHalls();
    this.subscribeToFilterChanges();
  }

  ngAfterViewInit(): void {
    this.townHalls.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public paginatorChange($event: PageEvent) {
    this.paginatorConfig.pageIndex = $event.pageIndex;
    this.paginatorConfig.pageSize  = $event.pageSize;
    this.paginatorConfig.length    = $event.length;

    this.requestFilters.page     = this.paginatorConfig.pageIndex + 1;
    this.requestFilters.per_page = this.paginatorConfig.pageSize;
    this.fetchTownHalls();
  }

  public sortData(sort) {
    this.requestFilters.sort_by = sort.active ? sort.active : this.defaultSort.active;
    this.requestFilters.order   = sort.direction ? sort.direction : this.defaultSort.direction;

    this.fetchTownHalls();
  }

  private fetchTownHalls() {
    this.isLoading++;
    this.townHallService.index(this.requestFilters, ['address']).pipe(finalize(() => this.isLoading--)).subscribe(
        result => {
          this.townHalls              = new MatTableDataSource<TownHall>(result.data);
          this.townHalls.sort         = this.sort;
          this.paginatorConfig.length = result.meta.total;
          this.totalResults           = result.meta.total;
          this.totalPages             = result.meta.last_page;
        },
        error => console.error(error),
    );
  }

  private subscribeToFilterChanges() {
    this.subscriptions.push(
        this.searchFilter.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe((searchTerm: string) => {
          this.requestFilters.search = searchTerm;
          this.handleFilters();
        }),
    );
  }

  // On filter change
  public handleFilters(): void {
    // this.caseListFilter = $event;
    this.resetPagination();
    this.resetSort();
    this.fetchTownHalls();
  }

  public deleteTownHall(townHallId: number) {
    Swal.fire({
      text:               this.translate.instant('SHARED.action.delete_confirmation',
          {model: this.translate.instant('LEGAL_ENTITY.town_hall.model_name.singular')}),
      icon:               'warning',
      showCancelButton:   false,
      confirmButtonText:  'OK',
      confirmButtonColor: '#4267b2',
    }).then(res => {
      if (res.isConfirmed) {
        this.isLoading++;
        this.townHallService.delete(townHallId).pipe(finalize(() => this.isLoading--)).subscribe(
            () => {
              const localTownHalls = this.townHalls.data;
              const localTeamIndex = localTownHalls.findIndex(sTeam => sTeam.id === townHallId);
              if (localTeamIndex >= 0) {
                localTownHalls.splice(localTeamIndex, 1);
              } else {
                console.error('Deleted team not in list');
              }

              this.townHalls = new MatTableDataSource<TownHall>(localTownHalls);
              this.toastr.success(
                  this.translate.instant('SHARED.submit_result.delete.success',
                      {model: this.translate.instant('LEGAL_ENTITY.town_hall.model_name.singular')},
                  ),
              );
            },
            () => this.toastr.error(this.translate.instant('SHARED.went-wrong')),
        );
      }
    });
  }

  private resetPagination(): void {
    this.paginatorConfig         = this.defaultPaginatorConfig;
    this.requestFilters.per_page = this.paginatorConfig.pageSize;
    this.requestFilters.page     = this.paginatorConfig.pageIndex;
  }

  private resetSort(): void {
    this.requestFilters.sort_by = this.defaultSort.active;
    this.requestFilters.order   = this.defaultSort.direction;
  }
}
